import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  Modal,
  ModalOpenProcess,
  ModalOpenProcessRef,
  Autocomplete,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  Typography,
  Autocomplete as MuiAutocomplete,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { selectLabelForBuilding } from "../helper.js";
import { BuildingCreateModal } from "./BuildingCreateModal.js";
import {
  BuildingSelectModal_BuildingFragment,
  BuildingSelectModal_ItemFragment,
  useBuildingSelectModalQuery,
} from "./BuildingSelectModal.generated.js";
import { assertNever } from "../../../utils.js";

type BuildingOption = BuildingSelectModal_BuildingFragment & {
  isLinked: boolean | null | undefined;
};

interface Props {
  id?: string;
  title?: string;
  crmOrganisation?: {
    id: string;
    mode: "sort-top" | "exclude";
  };
  hidePlacementSelect?: true;
  primaryActionLabel?: string;
  primaryActionDisabled?: boolean;
  canCreateBuilding?: boolean;
  onCreateRefetchQueries?: string[];
  preselectedBuildingId?: string | null | undefined;
  preselectedBuildingItemId?: string | null | undefined;
  handleClose: () => void;
  handleComplete: (
    building: Omit<BuildingSelectModal_BuildingFragment, "items">,
    buildingItem: BuildingSelectModal_ItemFragment,
    handleClose: () => void
  ) => Promise<void> | void;
  closeAfterCreate?: boolean;
}

export function BuildingSelectModal({
  id,
  title,
  crmOrganisation,
  primaryActionLabel,
  primaryActionDisabled,
  canCreateBuilding = true,
  onCreateRefetchQueries,
  hidePlacementSelect,
  preselectedBuildingId,
  preselectedBuildingItemId,
  handleClose,
  handleComplete,
  closeAfterCreate,
}: Props) {
  const { t } = useTranslate(["Buildings", "BuildingSelectBox", "Global"]);

  const [buildingId, setBuildingId] = React.useState(
    preselectedBuildingId ?? ""
  );
  const [buildingItemId, setBuildingItemId] = React.useState(
    preselectedBuildingItemId ?? ""
  );
  const [inputValue, setInputValue] = React.useState<string | null>(null);

  const variables = !crmOrganisation
    ? {}
    : crmOrganisation.mode === "sort-top"
      ? {
          hoistIfLinkedToCrmOrganisationId: crmOrganisation.id,
        }
      : crmOrganisation.mode === "exclude"
        ? {
            excludeCrmOrganisationId: crmOrganisation.id,
          }
        : assertNever(crmOrganisation.mode);

  const client = useApolloClient();
  const query = useBuildingSelectModalQuery({
    client,
    variables: {
      ...variables,
      search: inputValue,
    },
  });

  const buildings = React.useMemo(
    () =>
      getDataOrNull(query.data?.buildings)?.edges.map(edge => ({
        ...edge.node.building,
        isLinked: edge.node.isLinked,
      })) ?? [],
    [query.data?.buildings]
  );
  const selectedBuilding = React.useMemo(
    () => buildings.find(b => b.id === buildingId),
    [buildingId, buildings]
  );

  const buildingOptions = buildings ?? [];

  const placements = React.useMemo(
    () => buildings?.find(b => b.id === buildingId)?.items ?? [],
    [buildingId, buildings]
  );

  const processRef = React.useRef<ModalOpenProcessRef>();

  return (
    <Modal
      id={id}
      title={title ?? t("Select building", { ns: "Buildings" })}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Cancel", {
            ns: "Global",
          }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
        {
          label:
            primaryActionLabel ??
            t("Select", {
              ns: "Global",
            }),
          buttonProps: {
            disabled: !buildingItemId || query.loading || primaryActionDisabled,
            onClick: async () => {
              const building = buildings?.find(
                building => building.id === buildingId
              );
              const buildingItem = building?.items.find(
                item => item.id === buildingItemId
              );

              if (!building || !buildingItem)
                throw new Error("Building or building item missing");

              await handleComplete(building, buildingItem, handleClose);
            },
          },
        },
      ]}
    >
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1}>
          <MuiAutocomplete<BuildingOption, false, boolean, false>
            options={buildingOptions}
            getOptionLabel={option => selectLabelForBuilding(option)}
            noOptionsText={
              <Stack
                direction={"column"}
                spacing={1}
                sx={{
                  alignItems: "center",
                }}
              >
                <Typography align="center">
                  {t("No buildings found", { ns: "BuildingSelectBox" })}
                </Typography>
                {canCreateBuilding && (
                  <Button onClick={() => processRef.current?.open()}>
                    <AddIcon sx={{ mr: 0.5 }} color="secondary" />
                    <Typography
                      color="textSecondary"
                      sx={{
                        fontWeight: "500",
                      }}
                    >
                      {t("Add new building", { ns: "BuildingSelectBox" })}
                    </Typography>
                  </Button>
                )}
              </Stack>
            }
            filterOptions={option => option}
            groupBy={
              crmOrganisation?.mode === "sort-top"
                ? option =>
                    option.isLinked
                      ? t("Linked", { ns: "Buildings" })
                      : t("Other", { ns: "Global" })
                : undefined
            }
            value={selectedBuilding ?? null}
            renderInput={({
              slotProps: paramsSlotProps,
              disabled,
              fullWidth,
              id,
              size,
            }) => {
              const value = selectedBuilding
                ? selectLabelForBuilding(selectedBuilding)
                : (inputValue ?? "");

              return (
                <TextField
                  id={id}
                  label={t("Select an existing building", {
                    ns: "BuildingSelectBox",
                  })}
                  slotProps={{
                    htmlInput: {
                      ...paramsSlotProps.htmlInput,
                      value,
                      onChange: (
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        paramsSlotProps.htmlInput.onChange?.(event);
                        setInputValue(event.target.value);
                      },
                    },
                    input: paramsSlotProps.input,
                    inputLabel: paramsSlotProps.inputLabel,
                  }}
                  autoFocus={true}
                  disabled={disabled}
                  fullWidth={fullWidth}
                  size={size}
                />
              );
            }}
            onInputChange={(event, newInputValue, reason) => {
              console.log(newInputValue, reason);
              if (reason === "clear") {
                setInputValue("");
                setBuildingId("");
                setBuildingItemId("");
              }
              if (reason === "input") {
                setInputValue(newInputValue);
                setBuildingId("");
                setBuildingItemId("");
              }
            }}
            onChange={(event, value) => {
              event.stopPropagation();

              if (value && "__typename" in value) {
                setBuildingId(value?.id ?? "");
                setBuildingItemId(value?.rootItem?.id ?? "");
              } else if (!value) {
                setBuildingId("");
                setBuildingItemId("");
              }
            }}
          />
          {canCreateBuilding && (
            <IconButton
              color="primary"
              size="large"
              data-testid="add-address-button"
              onClick={() => processRef.current?.open()}
            >
              <AddIcon />
            </IconButton>
          )}
        </Stack>
        {!hidePlacementSelect ? (
          <Autocomplete
            inputLabel={t("Placement", { ns: "BuildingSelectBox" })}
            value={
              placements.find(placement => placement.id === buildingItemId) ??
              null
            }
            required
            disabled={!buildingId || placements.length === 0}
            getOptionLabel={option =>
              `${option.pathForPdf ? `${option.pathForPdf} ` : ""}${
                option.title
              }`
            }
            options={placements}
            onChange={value => {
              const newPlacement = value;
              setBuildingItemId(
                placements.find(placement => placement.id === newPlacement?.id)
                  ?.id ?? ""
              );
            }}
          />
        ) : null}
        <ModalOpenProcess
          ref={processRef}
          Modal={BuildingCreateModal}
          modalProps={{
            id: "building-create-modal",
            crmOrganisationId: crmOrganisation?.id,
            handleComplete: async (
              building,
              buildingRootItemId,
              closeCreateModal
            ) => {
              setBuildingId(building.id);
              setBuildingItemId(buildingRootItemId);
              closeCreateModal();
              if (closeAfterCreate) {
                handleClose();
              }
            },
            refetchQueries: [
              namedOperations.Query.BuildingSelectModal,
              ...(onCreateRefetchQueries ?? []),
            ],
          }}
        />
      </Stack>
    </Modal>
  );
}
